// Safety net for the disclosure animation: the browser hides <details> content the
// moment `open` goes away, so a missed transitionend would leave a panel collapsed
// but still open. Longer than --duration-default in component-facets.scss.
const FACET_DISCLOSURE_FALLBACK = 600;

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.closeFacets = this.closeFacets.bind(this);
    this.onDisclosureClick = this.onDisclosureClick.bind(this);
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    this.facetButtons = document.querySelectorAll(".facets-button-show");
    this.productGrid = document.getElementById("ProductGridContainer");
    this.facetForm = this.querySelector("form");
    this.facetsWrapper = this.querySelector(".facets__wrapper");
    this.closeButton = this.querySelector(".facets__close");
    this.facetsOverlay = this.querySelector(".facets__overlay");
    this.facetSubmitButton = this.querySelector(".facets__submit");

    this.facetForm?.addEventListener(
      "input",
      this.debouncedOnSubmit.bind(this)
    );

    this.facetsWrapper?.addEventListener(
      "keydown",
      this.onHandleKeyDown.bind(this)
    );

    this.facetButtons.forEach((button) =>
      button.addEventListener("click", this.handleShowButtonsClick.bind(this))
    );

    this.closeButton?.addEventListener("click", this.closeFacets);
    this.facetsOverlay?.addEventListener("click", this.closeFacets);
    this.facetSubmitButton?.addEventListener("click", this.closeFacets);

    // Delegated: a filter refresh replaces the disclosure markup, the form stays.
    this.addEventListener("click", this.onDisclosureClick);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static setLoading(isLoading) {
    document
      .getElementById("ProductGridContainer")
      ?.querySelector(".collection")
      ?.classList.toggle("loading", isLoading);

    document.querySelectorAll(".facets__reset").forEach((btn) => {
      btn.classList.toggle("loading", isLoading);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();

    FacetFiltersForm.setLoading(true);

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.abortController?.abort();
      FacetFiltersForm.abortController = null;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    FacetFiltersForm.abortController = new AbortController();

    fetch(url, { signal: FacetFiltersForm.abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
      })
      .catch((error) => {
        if (error.name === "AbortError") return;
        console.error("Error fetching filters:", error);
        FacetFiltersForm.setLoading(false);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
  }

  static renderProductGridContainer(html) {
    const container = document.getElementById("ProductGridContainer");
    const parsedContainer = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductGridContainer");
    if (!container || !parsedContainer) return;
    container.innerHTML = parsedContainer.innerHTML;

    if (
      document.querySelector(".js-load-more") ||
      document.querySelector(".js-infinite-scroll")
    ) {
      initLoadMore();
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");
    const filterFocus = FacetFiltersForm.getFilterFocus();

    const facetDetailsElements = parsedHTML.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormProductType .js-filter"
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter
        ? element.dataset.index === jsFilter.dataset.index
        : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) =>
        !matchesIndex(element) ||
        element.closest("#FacetFiltersFormProductType")
    );

    const sourceCurrentFilter =
      Array.from(facetDetailsElements).find(matchesIndex);
    const targetCurrentFilter = event
      ? event.target.closest(".js-filter")
      : null;

    facetsToRender.forEach((element) => {
      const target = document.querySelector(
        `.js-filter[data-index="${element.dataset.index}"]`
      );
      if (!target) return;
      target.innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderProductCount(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (sourceCurrentFilter && targetCurrentFilter) {
      FacetFiltersForm.updateCurrentFilter(
        sourceCurrentFilter,
        targetCurrentFilter
      );
    }

    FacetFiltersForm.restoreFilterFocus(filterFocus);
    FacetFiltersForm.setLoading(false);
  }

  // The refetch swaps the markup of every filter but the one being edited. When that
  // removes the focused control, focus drops to <body> and the next Tab skips the rest
  // of that filter, so the control is remembered here and its counterpart refocused.
  static getFilterFocus() {
    const activeEl = document.activeElement;
    const filter = activeEl?.closest(".js-filter[data-index]");
    if (!filter) return null;

    let selector = null;
    if (activeEl.id) {
      selector = `#${CSS.escape(activeEl.id)}`;
    } else if (activeEl.matches(".facets__summary")) {
      selector = ".facets__summary";
    } else if (activeEl.closest("facet-remove")) {
      selector = "facet-remove a";
    }

    return { filter, selector };
  }

  static restoreFilterFocus(filterFocus) {
    if (!filterFocus) return;
    if (document.activeElement && document.activeElement !== document.body) {
      return;
    }

    // A value disabled by the new counts, or a Reset hidden again, cannot take focus,
    // so the filter's summary holds the place in the tab order instead.
    const { filter, selector } = filterFocus;
    const candidates = [
      selector ? filter.querySelector(selector) : null,
      filter.querySelector(".facets__summary"),
    ];
    for (const el of candidates) {
      if (!el) continue;
      el.focus({ preventScroll: true });
      if (document.activeElement === el) return;
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [".active-facets"];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const destinationElement = document.querySelector(selector);
      if (!activeFacetsElement || !destinationElement) return;
      destinationElement.innerHTML = activeFacetsElement.innerHTML;
    });
  }

  static renderProductCount(html) {
    const sourceCount = html.getElementById("ProductCount");
    const destinationCount = document.getElementById("ProductCount");
    if (sourceCount && destinationCount) {
      destinationCount.innerHTML = sourceCount.innerHTML;
    }
  }

  static renderAdditionalElements(html) {
    if (!html) return;

    // Active-filter counters (top-bar badge + drawer headline bracket).
    [".filters-count", ".facets__inner-title-count"].forEach((selector) => {
      const source = html.querySelector(selector);
      const destination = document.querySelector(selector);
      if (!source || !destination) return;

      destination.innerHTML = source.innerHTML;
      destination.classList.toggle(
        "hidden",
        source.classList.contains("hidden")
      );
    });

    // Search page: the result-count title and the result-type tabs both sit outside
    // #ProductGridContainer, so a filtered refetch has to re-render them explicitly.
    ["[data-search-title]", "[data-search-tabs]"].forEach((selector) => {
      const destination = document.querySelector(selector);
      if (!destination) return;

      const source = html.querySelector(selector);
      destination.innerHTML = source ? source.innerHTML : "";
    });

    // Clear All shows only while at least one filter is applied.
    const sourceClearAll = html.querySelector(".facets__clear-all");
    const destinationClearAll = document.querySelector(".facets__clear-all");
    if (sourceClearAll && destinationClearAll) {
      destinationClearAll.classList.toggle(
        "hidden",
        sourceClearAll.classList.contains("hidden")
      );
    }

    // "Show results [N]" label.
    const sourceSubmit = html.querySelector(".facets__submit .button__label");
    const destinationSubmit = document.querySelector(
      ".facets__submit .button__label"
    );
    if (sourceSubmit && destinationSubmit) {
      destinationSubmit.textContent = sourceSubmit.textContent;
      destinationSubmit.setAttribute(
        "data-label",
        sourceSubmit.getAttribute("data-label") || ""
      );
    }

    // Selected-value count next to a list filter label — the clicked filter is
    // not re-rendered, so its bracket has to be synced explicitly.
    html.querySelectorAll(".js-filter[data-index]").forEach((sourceFilter) => {
      const sourceFilterCount = sourceFilter.querySelector(
        ".facets__summary-count"
      );
      const destinationFilterCount = document.querySelector(
        `.js-filter[data-index="${sourceFilter.dataset.index}"] .facets__summary-count`
      );
      if (!sourceFilterCount || !destinationFilterCount) return;

      destinationFilterCount.innerHTML = sourceFilterCount.innerHTML;
      destinationFilterCount.classList.toggle(
        "hidden",
        sourceFilterCount.classList.contains("hidden")
      );
    });
  }

  static updateCurrentFilter(source, target) {
    const sourceCurrentFilterReset = source.querySelector("facet-remove");
    const targetCurrentFilterReset = target.querySelector("facet-remove");
    if (sourceCurrentFilterReset && targetCurrentFilterReset) {
      targetCurrentFilterReset.outerHTML = sourceCurrentFilterReset.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`
    );
  }

  static getSections() {
    const productGrid = document.getElementById("product-grid");
    if (!productGrid) return [];
    return [{ section: productGrid.dataset.id }];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll(
      "facet-filters-form form"
    );
    const isQuickFilter = this.facetForm?.id === "FacetFiltersFormProductType";
    const forms = [];

    sortFilterForms.forEach((form) => {
      if (form.id === "FacetSortForm" || form.id === "FacetFiltersForm") {
        if (form.id === "FacetFiltersForm" && isQuickFilter) {
          // Strip all params the quick filter controls (by input name, not current value) so unchecked state also removes the param from the main form
          const quickParamNames = new Set(
            Array.from(this.facetForm.querySelectorAll("input[name]")).map(
              (input) => input.name
            )
          );
          const mainParams = new URLSearchParams(this.createSearchParams(form));
          for (const key of quickParamNames) {
            mainParams.delete(key);
          }
          const paramStr = mainParams.toString();
          if (paramStr) forms.push(paramStr);
        } else {
          // The sort form is left empty while its control is hosted by the filter panel,
          // and an empty part would only add a stray separator to the query.
          const params = this.createSearchParams(form);
          if (params) forms.push(params);
        }
      }
      if (form.id === "FacetFiltersFormProductType" && isQuickFilter) {
        forms.push(this.createSearchParams(form));
      }
    });

    this.onSubmitForm(forms.join("&"), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();

    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf("?") + 1
          );
    FacetFiltersForm.renderPage(url);
  }

  onDisclosureClick(event) {
    const summary = event.target.closest(".facets__summary");
    if (!summary) return;

    // The toggle is ours in every case: the per-filter Reset link sits inside the
    // summary and must not fold the filter it has just cleared.
    event.preventDefault();
    if (event.target.closest("facet-remove")) return;

    const details = summary.closest(".facets__disclosure");
    const display = details?.querySelector(".facets__display");
    if (!details || !display || details.dataset.animating) return;

    FacetFiltersForm.toggleDisclosure(details, display, !details.open);
  }

  static toggleDisclosure(details, display, shouldOpen) {
    details.dataset.animating = shouldOpen ? "opening" : "closing";

    // Measure at rest: closed content has no height, so `open` comes first.
    if (shouldOpen) details.open = true;

    const height = `${display.scrollHeight}px`;
    const collapsed = { height: "0px", opacity: "0" };
    const expanded = { height, opacity: "1" };

    // A panel that has just been revealed has no previous rendered style to
    // transition from, so the starting values land with transitions switched off.
    display.style.transition = "none";
    display.style.overflow = "hidden";
    Object.assign(display.style, shouldOpen ? collapsed : expanded);
    void display.offsetHeight;
    display.style.removeProperty("transition");
    Object.assign(display.style, shouldOpen ? expanded : collapsed);

    let fallbackTimer = null;

    const finish = () => {
      clearTimeout(fallbackTimer);
      display.removeEventListener("transitionend", onTransitionEnd);
      if (!shouldOpen) details.open = false;

      // Give the panel back to CSS without animating out of the inline values —
      // dropping a pixel height would otherwise transition into the auto one.
      display.style.transition = "none";
      ["height", "opacity", "overflow"].forEach((property) =>
        display.style.removeProperty(property)
      );
      void display.offsetHeight;
      display.style.removeProperty("transition");
      delete details.dataset.animating;
    };

    const onTransitionEnd = (event) => {
      if (event.target !== display || event.propertyName !== "height") return;
      finish();
    };

    display.addEventListener("transitionend", onTransitionEnd);
    fallbackTimer = setTimeout(finish, FACET_DISCLOSURE_FALLBACK);
  }

  handleFacetButtonClick(event) {
    event.preventDefault();

    if (!this.facetButtons.length > 0 || !this.facetForm) return;

    this.facetsWrapper.classList.add("active");

    this.focusElement = event.target.closest(".facets-button-show")
      ? event.target
      : document.activeElement;

    if (this.classList.contains("facets--drawer")) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.add("overflow-hidden-mobile-facets");
    }

    this.addEventListener(
      "transitionend",
      () => {
        trapFocus(this, this.querySelector('[role="dialog"]'));
      },
      { once: true }
    );
  }

  closeFacets(event) {
    event.preventDefault();

    if (!this.facetsWrapper) return;

    this.facetsWrapper.classList.remove("active");
    removeTrapFocus(this.focusElement);
    document.body.classList.remove("overflow-hidden");
    document.body.classList.remove("overflow-hidden-mobile-facets");
  }

  onHandleKeyDown(event) {
    if (
      event.key === "Escape" &&
      this.facetsWrapper?.classList.contains("active")
    ) {
      this.closeFacets(event);
    }
  }

  handleShowButtonsClick(event) {
    event.preventDefault();

    // targetId - FacetFiltersForm or FacetSortForm
    const targetId =
      event.currentTarget.dataset.desktopModal ||
      event.currentTarget.dataset.mobileModal;

    const targetForm = document.querySelector(
      `[data-filters-form="${targetId}"]`
    );
    if (!targetForm) return;
    targetForm.handleFacetButtonClick(event);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.abortController = null;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("input").forEach((element) =>
      element.addEventListener("change", this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
    this.controlSlider();
    this.controlInput();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll(".field__input");
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (!minInput || !maxInput) return;
    if (maxInput.value) minInput.setAttribute("max", maxInput.value);
    if (minInput.value) maxInput.setAttribute("min", minInput.value);
    if (minInput.value === "") {
      maxInput.setAttribute("min", 0);
    }
    if (maxInput.value === "") {
      minInput.setAttribute("max", maxInput.getAttribute("max"));
    }
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute("min"));
    const max = Number(input.getAttribute("max"));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }

  fillSlider() {
    const inputRangeWrappers = document.querySelectorAll(
      ".facets__price .facets__range"
    );
    inputRangeWrappers.forEach((inputWrapper) => {
      const inputsRange = inputWrapper.querySelectorAll(".field__range");
      const inputRangeFrom = inputsRange[0];
      const inputRangeTo = inputsRange[1];

      const range = Number(inputRangeTo.max) - Number(inputRangeTo.min);
      const fromCurrent =
        Number(inputRangeFrom.value) - Number(inputRangeTo.min);
      const toCurrent = Number(inputRangeTo.value) - Number(inputRangeTo.min);
      const minRange = (fromCurrent / range) * 100;
      const maxRange = (toCurrent / range) * 100;

      inputWrapper.setAttribute(
        "style",
        `--range-min: ${minRange}%; --range-max: ${maxRange}%`
      );
    });
  }

  controlSlider() {
    const inputRangeWrappers = document.querySelectorAll(
      ".facets__price .facets__range"
    );
    const inputNumberWrappers = document.querySelectorAll(
      ".facets__price .facets__price-wrapper"
    );

    inputRangeWrappers.forEach((inputWrapper, index) => {
      // A collection with no price range renders the fields without the slider.
      if (!inputNumberWrappers[index]) return;

      const inputsRange = inputWrapper.querySelectorAll(".field__range");
      const inputRangeFrom = inputsRange[0];
      const inputRangeTo = inputsRange[1];
      const inputNumberFrom =
        inputNumberWrappers[index].querySelectorAll(".field__input")[0];
      const inputNumberTo =
        inputNumberWrappers[index].querySelectorAll(".field__input")[1];

      inputRangeFrom.oninput = () => {
        const from = Number(inputRangeFrom.value);
        const to = Number(inputRangeTo.value);
        if (from > to) {
          inputRangeFrom.value = to;
          inputNumberFrom.value = to;
        } else {
          inputNumberFrom.value = from;
        }

        this.fillSlider();
      };

      if (Number(inputRangeTo.value) <= 0) {
        inputRangeTo.style.zIndex = 2;
      } else {
        inputRangeTo.style.zIndex = 0;
      }

      inputRangeTo.oninput = () => {
        const from = Number(inputRangeFrom.value);
        const to = Number(inputRangeTo.value);
        if (from <= to) {
          inputRangeTo.value = to;
          inputNumberTo.value = to;
        } else {
          inputNumberTo.value = from;
          inputRangeTo.value = from;
        }

        if (Number(inputRangeTo.value) <= 0) {
          inputRangeTo.style.zIndex = 2;
        } else {
          inputRangeTo.style.zIndex = 0;
        }

        this.fillSlider();
      };
    });
  }

  controlInput() {
    const inputRangeWrappers = document.querySelectorAll(
      ".facets__price .facets__range"
    );
    const inputNumberWrappers = document.querySelectorAll(
      ".facets__price .facets__price-wrapper"
    );

    inputNumberWrappers.forEach((inputWrapper, index) => {
      if (!inputRangeWrappers[index]) return;

      const inputsNumber = inputWrapper.querySelectorAll(".field__input");
      const inputNumberFrom = inputsNumber[0];
      const inputNumberTo = inputsNumber[1];
      const inputRangeFrom =
        inputRangeWrappers[index].querySelectorAll(".field__range")[0];
      const inputRangeTo =
        inputRangeWrappers[index].querySelectorAll(".field__range")[1];

      inputNumberFrom.oninput = () => {
        const from = Number(inputNumberFrom.value);
        const to = Number(inputNumberTo.value);

        if (to && from > to) {
          inputRangeFrom.value = to;
        } else {
          inputRangeFrom.value = from;
        }

        this.fillSlider();
      };

      inputNumberTo.oninput = () => {
        const from = Number(inputNumberFrom.value);
        const to = Number(inputNumberTo.value);

        if (to >= from) {
          inputRangeTo.value = to;
        } else {
          inputRangeTo.value = from;
        }

        this.fillSlider();
      };

      inputNumberFrom.onblur = () => {
        if (inputNumberFrom.value === "") return;
        const from = Number(inputNumberFrom.value);
        const min = Number(inputNumberFrom.getAttribute("min"));
        const max = Number(inputNumberFrom.getAttribute("max"));

        if (from < min) {
          inputRangeFrom.value = min;
          inputNumberFrom.value = min;
        } else {
          inputRangeFrom.value = from;
        }

        this.fillSlider();
      };

      inputNumberTo.onblur = () => {
        if (inputNumberTo.value === "") return;
        const to = Number(inputNumberTo.value);
        const min = Number(inputNumberTo.getAttribute("min"));
        const max = Number(inputNumberTo.getAttribute("max"));

        if (to > max) {
          inputRangeTo.value = max;
          inputNumberTo.value = max;
        } else {
          inputRangeTo.value = to;
        }

        this.fillSlider();
      };
    });
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    facetLink.setAttribute("role", "button");
    facetLink.addEventListener("click", this.closeFilter.bind(this));
    facetLink.addEventListener("keyup", (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form =
      this.closest("facet-filters-form") ||
      document.querySelector("facet-filters-form");
    form.onActiveFilterClick(event);
  }
}

customElements.define("facet-remove", FacetRemove);
